/**
 * «Шаблоны AI» — выверенные ответы робота «Чатов» на частые вопросы. Детектор узнаёт вопрос клиента и, если
 * у него есть шаблон, черновик пишется по шаблону: текст дословно, метки кода ({ссылка}, {адрес}, {часы})
 * подставляет код, остальные метки модель раскрывает по инструкции. Любой с `client_chats` добавляет, правит,
 * убирает в архив и возвращает; каждая правка — версия (кто, когда, почему), есть откат и «Проба».
 * Вид — по концепту Codex `web/public/assets/design/ai-templates-concept.html`
 * (`docs/specs/2026-11-04-ai-templates-ux-design.md`); спека `docs/specs/2026-11-04-chat-ai-templates-design.md`.
 */
import { useGetIdentity } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, ArrowLeft, Clock, Link2, Loader2, MessageSquarePlus, Play, Plus, Search } from "lucide-react";
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { ApiFailure } from "@/lib/api";
import type { CurrentUser } from "@/lib/providers";
import {
  archiveAiTemplate,
  createAiTemplate,
  editAiTemplate,
  loadAiTemplates,
  loadAiVersions,
  probeAiTemplate,
  revertAiTemplate,
  type AiFields,
  type AiProbe,
  type AiTemplate,
} from "@/lib/aiTemplates";
import { AiTemplateRemarks } from "./AiTemplateRemarks";
import { RemarkForm } from "./VoiceLabRemarks";

type Get = (k: string) => string;
type Set = (p: Record<string, string>) => void;
type Notice = { text: string; kind: "good" | "error" | "warn" | "plain" } | null;

/* Классы концепта: кнопка 36 px, радиус 6, карточка 10, «primary» — рамка и фон акцента. */
const btn =
  "inline-flex min-h-9 items-center justify-center gap-1.5 rounded-[6px] border border-border bg-bg px-[11px] py-[7px] text-[13px] hover:bg-bg-hover disabled:cursor-not-allowed disabled:opacity-55";
const primary = `${btn} !border-accent !bg-accent-dim text-accent-text`;
const quiet = `${btn} !border-transparent !bg-transparent hover:!bg-bg-hover`;
const card = "rounded-[10px] border border-border bg-bg-subtle";
const field =
  "w-full rounded-[6px] border border-border bg-bg px-2.5 py-2 text-[13px] leading-[1.6] text-fg outline-none placeholder:text-fg-muted focus-visible:border-accent";
const mono = "font-mono tabular-nums";
const plain = (s: string) => s.toLowerCase().replaceAll("ё", "е");
/** Порядок сценариев — как в концепте. */
const ORDER = ["zapis", "oplata", "bron", "sdat", "zhaloba"];
const CODE_MARKS = ["{ссылка}", "{адрес}", "{часы}"];
const MIXED = "Как записаться и есть ли платье моего размера?";
const EMPTY: AiFields = { type: "zapis", question: "", examples: "", exclude: "", text: "", alt: "", instruction: "", kb_question_id: null };
const FIELDS: { key: keyof AiFields; label: string }[] = [
  { key: "type", label: "Сценарий" },
  { key: "question", label: "Вопрос клиента" },
  { key: "examples", label: "Как ещё спрашивают" },
  { key: "exclude", label: "Не применять, если" },
  { key: "text", label: "Текст шаблона" },
  { key: "alt", label: "Запасной текст" },
  { key: "instruction", label: "Инструкция модели" },
];
const ACTIONS: Record<string, string> = {
  create: "Создан",
  edit: "Изменён",
  archive: "Убран в архив",
  restore: "Возвращён из архива",
  revert: "Возвращено содержимое версии",
};

const pick = (t: AiFields): AiFields => ({
  type: t.type,
  question: t.question,
  examples: t.examples,
  exclude: t.exclude,
  text: t.text,
  alt: t.alt,
  instruction: t.instruction,
  kb_question_id: t.kb_question_id,
});
const same = (a: AiFields, b: AiFields) => JSON.stringify(pick(a)) === JSON.stringify(pick(b));
/** «04.11.26, 09:00» из «2026-11-04 09:00». */
const when = (at: string | null) => (at ? `${at.slice(8, 10)}.${at.slice(5, 7)}.${at.slice(2, 4)}, ${at.slice(11, 16)}` : "");
const who = (kind: string, name: string) => (kind === "model" ? "модель" : name || "—");

/** Текст с метками: метки кода — цветом акцента, метки модели — цветом «успеха»; смысл — в подсказке. */
function Tokens({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\{[^{}\s]{1,40}\})/).map((p, i) =>
        /^\{[^{}\s]{1,40}\}$/.test(p) ? (
          <span
            key={i}
            className={`rounded-[4px] px-[3px] py-px ${mono} ${CODE_MARKS.includes(p) ? "bg-accent-dim text-accent-text" : "bg-ok-bg text-ok"}`}
            title={CODE_MARKS.includes(p) ? "Подставляет код" : "Подставляет модель"}
          >
            {p}
          </span>
        ) : (
          <Fragment key={i}>{p}</Fragment>
        ),
      )}
    </>
  );
}

function Box({ notice, children }: { notice?: Notice; children?: ReactNode }) {
  const kind = notice?.kind ?? "plain";
  if (!notice && !children) return null;
  const tone = { good: "bg-ok-bg text-ok", error: "bg-err-bg text-err", warn: "bg-warn-bg text-warn", plain: "bg-bg-subtle" }[kind];
  return (
    <div role={kind === "error" ? "alert" : "status"} className={`rounded-[6px] border border-border p-3 text-[13px] [overflow-wrap:anywhere] ${tone}`}>
      {notice?.text}
      {children}
    </div>
  );
}

/** Поле текста по высоте содержимого: весь текст виден без прокрутки внутри поля (владелец 04.11.26). */
function AutoArea({
  id,
  value,
  onChange,
  rows,
  placeholder,
  invalid,
  describedBy,
  onPick,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  rows: number;
  placeholder?: string;
  invalid?: boolean;
  describedBy?: string;
  /** Выделенный в поле кусок (пусто — выделения нет): к нему — замечание для модели. */
  onPick?: ((text: string) => void) | undefined;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`; // + рамка: без неё последняя строка прячется
  }, [value]);
  return (
    <textarea
      ref={ref}
      id={id}
      value={value}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      onSelect={(e) => {
        const el = e.currentTarget;
        onPick?.(el.value.slice(el.selectionStart, el.selectionEnd).trim());
      }}
      placeholder={placeholder}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className={`${field} resize-none overflow-hidden ${invalid ? "!border-err" : ""}`}
    />
  );
}

/** Ссылка на открытый шаблон (с вкладкой) — переслать коллеге или модели. */
function CopyLink() {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      window.prompt("Ссылка на шаблон", window.location.href); // буфер недоступен — покажем ссылку
    }
  };
  return (
    <button type="button" className={quiet} onClick={() => void copy()} title={window.location.href}>
      <Link2 size={15} /> {done ? "Скопировано" : "Ссылка"}
    </button>
  );
}

export function AiTemplatesView({ get, set }: { get: Get; set: Set }) {
  const client = useQueryClient();
  const active = useQuery({ queryKey: ["ai-templates", false], queryFn: () => loadAiTemplates(false) });
  const archived = useQuery({ queryKey: ["ai-templates", true], queryFn: () => loadAiTemplates(true) });
  const [notice, setNotice] = useState<Notice>(null);
  const inArchive = get("archive") === "1";
  const scenario = get("type") || "zapis";
  const selected = get("t");
  const types = active.data?.types ?? {};
  const order = [...ORDER.filter((k) => k in types), ...Object.keys(types).filter((k) => !ORDER.includes(k))];
  const all = [...(active.data?.templates ?? []), ...(archived.data?.templates ?? [])];
  const refresh = () => client.invalidateQueries({ queryKey: ["ai-templates"] });

  if (selected) {
    const template = selected === "new" ? undefined : all.find((t) => String(t.id) === selected);
    if (selected !== "new" && !template) {
      return active.isLoading || archived.isLoading ? (
        <p className="text-[13px] text-fg-muted">Загружаю шаблон…</p>
      ) : (
        <Box notice={{ text: `Шаблона #${selected} нет.`, kind: "error" }}>
          <div className="mt-2">
            <button type="button" className={btn} onClick={() => set({ t: "", pane: "" })}>
              К списку
            </button>
          </div>
        </Box>
      );
    }
    return (
      <div className="@container">
        <Editor
          key={selected}
          template={template}
          defaultType={inArchive ? "zapis" : scenario}
          types={types}
          order={order}
          cities={active.data?.cities ?? []}
          others={active.data?.templates ?? []}
          pane={get("pane")}
          setPane={(pane) => set({ pane })}
          close={(text) => {
            setNotice(text);
            set({ t: "", pane: "" });
          }}
          opened={(id) => set({ t: String(id), pane: "" })}
          refresh={refresh}
        />
      </div>
    );
  }

  const pool = inArchive ? (archived.data?.templates ?? []) : (active.data?.templates ?? []).filter((t) => t.type === scenario);
  const words = plain(get("q")).trim().split(/\s+/).filter(Boolean);
  const shown = pool.filter((t) =>
    words.every((w) =>
      plain([t.id, t.question, t.text, t.examples, t.exclude, t.alt, t.instruction, t.author, `v${t.version}`].join(" ")).includes(w),
    ),
  );
  const count = (k: string) => (active.data?.templates ?? []).filter((t) => t.type === k).length;
  const restore = async (t: AiTemplate) => {
    try {
      const r = await archiveAiTemplate(t.id, false, t.version);
      setNotice({ text: `Шаблон возвращён · v${r.version}`, kind: "good" });
      await refresh();
    } catch (e) {
      setNotice({ text: e instanceof Error ? e.message : String(e), kind: "error" });
    }
  };

  return (
    <div className="@container flex flex-col gap-4" data-annot="шаблоны AI">
      <Box notice={notice} />
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex min-h-9 flex-1 basis-full items-center gap-2 rounded-[6px] border border-border bg-bg pl-2.5 focus-within:border-accent @xl:max-w-[340px] @xl:basis-auto">
          <Search size={16} className="flex-none text-fg-muted" />
          <span className="sr-only">Поиск в выбранном сценарии</span>
          <input
            value={get("q")}
            onChange={(e) => set({ q: e.target.value })}
            placeholder="слова в вопросе или тексте"
            className="min-w-0 flex-1 bg-transparent py-2 pr-2.5 text-[13px] outline-none placeholder:text-fg-muted"
          />
        </label>
        <small className="text-[12px] text-fg-muted">
          {shown.length} из {pool.length} шаблонов
        </small>
        <button type="button" className={`${primary} w-full @xl:w-auto`} onClick={() => set({ t: "new", pane: "" })}>
          <Plus size={16} /> Добавить шаблон
        </button>
      </div>

      <div className="grid items-start gap-4 @2xl:grid-cols-[180px_minmax(0,1fr)] @4xl:grid-cols-[220px_minmax(0,1fr)]">
        <nav aria-label="Сценарии" className={`${card} grid grid-cols-2 gap-1 p-2 @2xl:grid-cols-1 @2xl:gap-[3px]`}>
          {order.map((k) => (
            <button
              key={k}
              type="button"
              aria-current={!inArchive && scenario === k}
              onClick={() => set({ type: k, archive: "", q: "" })}
              className={`${btn} !justify-between !border-transparent text-left text-[12.5px] ${
                !inArchive && scenario === k ? "!bg-accent-dim text-accent-text" : "!bg-transparent"
              }`}
            >
              <span>{types[k]}</span>
              <b className="font-normal whitespace-nowrap">{count(k)}</b>
            </button>
          ))}
          <button
            type="button"
            aria-current={inArchive}
            onClick={() => set({ archive: "1", q: "" })}
            className={`${btn} !justify-between !border-transparent text-left text-[12.5px] @2xl:mt-[7px] @2xl:rounded-none @2xl:!border-t-border @2xl:pt-3 ${
              inArchive ? "!bg-accent-dim text-accent-text" : "!bg-transparent"
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              <Archive size={16} /> Архив
            </span>
            <b className="font-normal">{archived.data?.templates.length ?? 0}</b>
          </button>
        </nav>

        <section className="grid gap-3" aria-label="Список шаблонов">
          <div>
            <h2 className="text-[14px] font-semibold">{inArchive ? "Архив" : types[scenario] ?? scenario}</h2>
            <small className="text-[12px] text-fg-muted">
              {inArchive ? "Робот не выбирает архивные шаблоны." : "Общие ответы робота. Выбор делает детектор, отправку подтверждает сотрудник."}
            </small>
          </div>
          {(active.error || archived.error) && <Box notice={{ text: `Не загрузилось: ${((active.error || archived.error) as Error).message}`, kind: "error" }} />}
          {active.data && shown.length === 0 ? (
            <div className={`${card} px-5 py-8 text-center`}>
              <h2 className="text-[14px] font-semibold">
                {words.length ? "Ничего не нашлось" : inArchive ? "В архиве пока пусто" : "Для этого сценария пока нет шаблонов"}
              </h2>
              <p className="mx-auto mt-2 mb-4 max-w-[52ch] text-fg-muted">
                {words.length
                  ? "Попробуйте другие слова. Поиск учитывает все слова в выбранном сценарии."
                  : inArchive
                    ? "Отправьте шаблон в архив из его карточки. История сохранится."
                    : "Пока робот отвечает по линии сценария. Добавьте первый выверенный ответ."}
              </p>
              {words.length ? (
                <button type="button" className={btn} onClick={() => set({ q: "" })}>
                  Сбросить поиск
                </button>
              ) : inArchive ? (
                <button type="button" className={btn} onClick={() => set({ archive: "" })}>
                  К действующим
                </button>
              ) : (
                <button type="button" className={primary} onClick={() => set({ t: "new", pane: "" })}>
                  <Plus size={16} /> Добавить шаблон
                </button>
              )}
            </div>
          ) : (
            <div className="grid items-start gap-2 @4xl:grid-cols-2">
              {shown.map((t) => (
                <article key={t.id} className={`${card} p-3`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`${mono} text-fg-muted`}>#{t.id}</span>
                    <span className={`${mono} rounded-[4px] bg-neutral-bg px-1.5 py-px text-[11.5px]`}>v{t.version}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => set({ t: String(t.id), pane: "" })}
                    className="min-h-8 text-left font-semibold text-fg hover:text-accent-text hover:underline"
                  >
                    {t.question}
                  </button>
                  <p className="mt-1.5 mb-3 line-clamp-3 whitespace-pre-line text-fg-muted [overflow-wrap:anywhere]">
                    <Tokens text={t.text} />
                  </p>
                  <div className="flex flex-wrap gap-1.5 text-[11.5px] text-fg-muted">
                    <span>{who(t.author_kind, t.author)}</span>
                    <time>{when(t.at)}</time>
                    {inArchive && <span>· {t.type_label}</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-2.5 gap-y-1.5 border-t border-border pt-2 text-[11.5px]">
                    <span>
                      Сработал <b className="font-semibold">{t.hits}</b>
                    </span>
                    <span>
                      Отправлен как есть <b className="font-semibold">{t.sent}</b>
                    </span>
                    <span>
                      Правили <b className="font-semibold">{t.edited}</b>
                    </span>
                    <span>
                      Отклонён <b className="font-semibold">{t.rejected}</b>
                    </span>
                    {t.deviated > 0 && (
                      <span className="text-warn">
                        Отступил от текста <b className="font-semibold">{t.deviated}</b>
                      </span>
                    )}
                  </div>
                  {inArchive && (
                    <div className="mt-2.5">
                      <button type="button" className={btn} onClick={() => void restore(t)}>
                        <ArchiveRestore size={16} /> Вернуть из архива
                      </button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
          {shown.length > 0 && (
            <small className="text-[12px] text-fg-muted">
              За 30 дней. «Правили» — черновик взяли в поле ответа; поменяли ли текст, отдельно не видно. Пробы в статистику не входят.
            </small>
          )}
        </section>
      </div>
    </div>
  );
}

type Problem = { message: string; field?: string | undefined; stale?: boolean };

function Editor({
  template,
  defaultType,
  types,
  order,
  cities,
  others,
  pane,
  setPane,
  close,
  opened,
  refresh,
}: {
  template: AiTemplate | undefined;
  defaultType: string;
  types: Record<string, string>;
  order: string[];
  cities: { id: number; name: string }[];
  others: AiTemplate[];
  pane: string;
  setPane: (p: string) => void;
  close: (notice: Notice) => void;
  opened: (id: number) => void;
  refresh: () => Promise<void>;
}) {
  // Форма и «основа» (версия, от которой правим) живут, пока открыт этот шаблон: обновление списка после
  // 409 не стирает ввод — шаблон приходит новой версией, а черновик остаётся на экране.
  const [form, setForm] = useState<AiFields>(template ? pick(template) : { ...EMPTY, type: defaultType });
  const [saved, setSaved] = useState<AiFields | null>(template ? pick(template) : null);
  const [base, setBase] = useState(template?.version ?? 0);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [confirm, setConfirm] = useState(false);
  const [leave, setLeave] = useState<(() => void) | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const dirty = saved ? !same(form, saved) : !same(form, { ...EMPTY, type: defaultType });
  const conflict = template !== undefined && template.version !== base;
  const { data: me } = useGetIdentity<CurrentUser>();
  const замечания = !!template && !!me?.permissions.includes("voice_lab"); // разбор — модель AI Lab
  // Выделенный кусок поля и открытая форма замечания: у поля (`key`) или к шаблону целиком (`*`).
  const [кусок, setКусок] = useState<{ key: string; text: string } | null>(null);
  const [замечание, setЗамечание] = useState<{ key: string; quote: string } | null>(null);
  const [записано, setЗаписано] = useState<{ key: string; id: number } | null>(null); // подтверждение у поля
  const формаЗамечания = (key: string) =>
    template && замечание?.key === key ? (
      <RemarkForm
        room={`tpl-${template.id}/v${template.version}`}
        template={{ id: template.id, version: template.version, quote: замечание.quote }}
        onClose={() => setЗамечание(null)}
        onSaved={(id) => {
          setКусок(null);
          setЗаписано({ key, id });
          setNotice({ text: `Замечание №${id} сохранено — разбор и ответ во вкладке «Замечания»`, kind: "good" });
        }}
      />
    ) : null;
  const tab =
    pane === "history" && template
      ? "history"
      : pane === "probe"
        ? "probe"
        : pane === "remarks" && замечания
          ? "remarks"
          : "edit";
  const marks = useMemo(() => {
    const found = `${form.text}\n${form.alt}`.match(/\{[^{}\s]{1,40}\}/g) ?? [];
    return [...new Set(found)].filter((m) => !CODE_MARKS.includes(m));
  }, [form.text, form.alt]);
  const undescribed = marks.filter((m) => !form.instruction.includes(m));
  // После смены рабочего экрана фокус — к заголовку (концепт, «Телефон и доступность»).
  useEffect(() => heading.current?.focus(), [tab]);

  const guard = (go: () => void) => (dirty ? setLeave(() => go) : go());
  const fail = (e: unknown) => {
    if (e instanceof ApiFailure) {
      setProblem({ message: e.message, field: e.items[0]?.field, stale: e.status === 409 });
      if (e.status === 409) void refresh(); // придёт новая версия — покажем сравнение, ввод останется
    } else setProblem({ message: e instanceof Error ? e.message : String(e) });
  };
  const applied = (t: AiTemplate, text: string) => {
    setForm(pick(t));
    setSaved(pick(t));
    setBase(t.version);
    setReason("");
    setProblem(null);
    setNotice({ text, kind: "good" });
  };
  const save = async () => {
    setProblem(null);
    setNotice(null);
    if (!form.question.trim() || !form.text.trim() || !form.instruction.trim()) {
      setProblem({ message: "Заполните вопрос, текст шаблона и инструкцию модели" });
      return;
    }
    if (undescribed.length) {
      setProblem({ message: `422 · Опишите, чем заменить ${undescribed[0]}, в инструкции модели`, field: "instruction" });
      return;
    }
    setBusy(true);
    try {
      if (!template) {
        const t = await createAiTemplate({ ...form, reason });
        await refresh();
        opened(t.id);
        return;
      }
      const t = await editAiTemplate(template.id, { ...form, reason, version: base });
      await refresh();
      applied(
        t,
        t.version === base
          ? "Содержание не изменилось: новая версия не создана."
          : `Сохранено · v${t.version} · ${t.author}. Робот использует эту версию.`,
      );
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const archive = async (on: boolean) => {
    if (!template) return;
    setBusy(true);
    try {
      const t = await archiveAiTemplate(template.id, on, base);
      await refresh();
      if (on) close({ text: "Шаблон перемещён в архив", kind: "good" });
      else applied(t, `Шаблон возвращён · v${t.version}`);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };
  const revert = async (number: number) => {
    if (!template) return;
    setBusy(true);
    try {
      const t = await revertAiTemplate(template.id, number, base);
      await refresh();
      applied(t, `Создана v${t.version} с содержимым v${number}`);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const rebase = () => {
    if (!template) return;
    setBase(template.version);
    setSaved(pick(template));
    setProblem(null);
    setNotice({
      text: `Основа обновлена до v${template.version}. Ваш черновик оставлен в форме. Сверьте все поля перед сохранением.`,
      kind: "plain",
    });
  };
  const show = (k: keyof AiFields, v: unknown) => (k === "type" ? (types[String(v)] ?? String(v)) : String(v ?? "") || "—");
  const differs = conflict && template ? FIELDS.filter((f) => String(template[f.key] ?? "") !== String(form[f.key] ?? "")) : [];

  const input = (key: "question" | "examples" | "exclude" | "text" | "alt" | "instruction", label: string, help: string, rows: number) => (
    <div className="grid gap-[5px]">
      <label htmlFor={`f-${key}`} className="font-semibold">
        {label}
      </label>
      <AutoArea
        id={`f-${key}`}
        value={form[key]}
        rows={rows}
        onChange={(v) => {
          setForm({ ...form, [key]: key === "question" ? v.replace(/\n/g, " ") : v });
          setNotice(null);
        }}
        invalid={problem?.field === key}
        describedBy={`f-${key}-help${problem?.field === key ? ` f-${key}-error` : ""}`}
        onPick={замечания ? (text) => setКусок(text ? { key, text } : null) : undefined}
      />
      {замечания && кусок?.key === key && замечание?.key !== key && (
        <div className="flex min-w-0 items-center gap-2 rounded-[6px] border border-accent bg-bg px-2 py-1 text-[12px]" data-annot="выделено для замечания">
          <span className="min-w-0 flex-1 truncate text-fg-muted" title={кусок.text}>
            Выделено: «{кусок.text.replace(/\s+/g, " ").slice(0, 200)}»
          </span>
          <button
            type="button"
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent px-3 py-0.5 font-medium text-white"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setЗамечание({ key, quote: `${label}: ${кусок.text}`.slice(0, 600) })}
          >
            <MessageSquarePlus size={13} aria-hidden /> Замечание к выделенному
          </button>
        </div>
      )}
      {замечание?.key === key && (
        <div className="grid gap-1">
          <div className="max-w-xl whitespace-pre-wrap border-l-2 border-accent pl-2 text-[12px] text-fg-muted">
            «{замечание.quote.length > 300 ? `${замечание.quote.slice(0, 300)}…` : замечание.quote}»
          </div>
          {формаЗамечания(key)}
        </div>
      )}
      {записано?.key === key && замечание?.key !== key && (
        <span className="text-[12px] text-ok">✓ Замечание №{записано.id} сохранено — ответ появится во вкладке «Замечания»</span>
      )}
      <small id={`f-${key}-help`} className="text-[12px] leading-[1.5] text-fg-muted">
        {help}
      </small>
      {problem?.field === key && (
        <p id={`f-${key}-error`} className="text-err">
          {problem.message}
        </p>
      )}
    </div>
  );
  const preview = (title: string, text: string, empty: string) => (
    <div>
      <div className="mb-1.5 font-medium">{title}</div>
      <div className="min-h-16 rounded-[6px] border border-border bg-bg-subtle p-3 whitespace-pre-wrap [overflow-wrap:anywhere]">
        {text ? <Tokens text={text} /> : <span className="text-fg-muted">{empty}</span>}
      </div>
    </div>
  );

  return (
    <div className="grid gap-4" data-annot="карточка шаблона AI">
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button type="button" className={quiet} onClick={() => guard(() => close(null))}>
            <ArrowLeft size={16} /> К списку
          </button>
          <small className={`text-[12px] text-fg-muted ${mono}`}>
            {template
              ? `#${template.id} · v${template.version} · ${who(template.author_kind, template.author)} · ${when(template.at)}`
              : "Новый общий шаблон"}
          </small>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 ref={heading} tabIndex={-1} className="text-[15px] font-semibold outline-none">
            {template ? template.question : "Добавить шаблон"}
          </h2>
          <div className="flex items-center gap-1">
            {template && <CopyLink />}
            {template &&
              (template.archived ? (
                <button type="button" className={btn} disabled={busy} onClick={() => void archive(false)}>
                  <ArchiveRestore size={16} /> Вернуть из архива
                </button>
              ) : (
                <button type="button" className={`${quiet} text-err`} onClick={() => setConfirm(true)}>
                  <Archive size={16} /> В архив
                </button>
              ))}
          </div>
        </div>
      </div>

      {template?.archived && (
        <Box notice={{ text: "Шаблон в архиве: робот его не выбирает. Для изменения сначала верните его из архива.", kind: "warn" }} />
      )}
      <Box notice={notice} />
      {problem && problem.field !== "instruction" && <Box notice={{ text: problem.message, kind: "error" }} />}
      {conflict && template && (
        <Box>
          <h3 className="font-semibold">Сравните правки перед сохранением</h3>
          <div className="mt-2.5 grid gap-3">
            {differs.length === 0 && <p className="text-fg-muted">Новая версия совпадает с вашим черновиком.</p>}
            {differs.map((f) => (
              <div key={f.key} className="grid gap-2 @3xl:grid-cols-2">
                <div>
                  <b>
                    {f.label} · новая v{template.version}
                  </b>
                  <div className="mt-1 min-h-10 rounded-[6px] border border-border bg-bg p-3 whitespace-pre-wrap [overflow-wrap:anywhere]">
                    {show(f.key, template[f.key])}
                  </div>
                </div>
                <div>
                  <b>Ваш черновик · от v{base}</b>
                  <div className="mt-1 min-h-10 rounded-[6px] border border-border bg-bg p-3 whitespace-pre-wrap [overflow-wrap:anywhere]">
                    {show(f.key, form[f.key])}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-fg-muted">Ваши поля остаются в форме. После обновления основы проверьте их и сохраните отдельной новой версией.</p>
          <div className="mt-2">
            <button type="button" className={primary} onClick={rebase}>
              Обновить основу, оставить мой черновик
            </button>
          </div>
        </Box>
      )}
      {confirm && template && (
        <Box notice={{ text: "", kind: "warn" }}>
          <h3 className="font-semibold">Убрать шаблон #{template.id} в архив?</h3>
          <p>
            Робот перестанет его выбирать. История сохранится; шаблон можно вернуть.
            {dirty && " Сначала сохраните или отмените правки."}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className={`${btn} text-err`} disabled={busy || dirty} onClick={() => void archive(true)}>
              Да, в архив
            </button>
            <button type="button" className={btn} onClick={() => setConfirm(false)}>
              Оставить действующим
            </button>
          </div>
        </Box>
      )}
      {leave && (
        <Box notice={{ text: "", kind: "warn" }}>
          <h3 className="font-semibold">Есть несохранённые изменения</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className={primary} onClick={() => setLeave(null)}>
              Продолжить редактирование
            </button>
            <button
              type="button"
              className={btn}
              onClick={() => {
                const go = leave;
                setLeave(null);
                go();
              }}
            >
              Отменить изменения и перейти
            </button>
          </div>
        </Box>
      )}

      <nav aria-label="Карточка шаблона" className="flex gap-1 border-b border-border">
        {[
          { id: "edit", label: "Редактор", icon: null },
          ...(template ? [{ id: "history", label: "История", icon: <Clock size={16} /> }] : []),
          { id: "probe", label: "Проба", icon: <Play size={16} /> },
          ...(замечания ? [{ id: "remarks", label: "Замечания", icon: <MessageSquarePlus size={16} /> }] : []),
        ].map((x) => (
          <button
            key={x.id}
            type="button"
            aria-current={tab === x.id}
            onClick={() => setPane(x.id === "edit" ? "" : x.id)}
            className={`inline-flex min-h-9 items-center gap-1.5 rounded-t-[6px] border-b-2 px-[11px] py-[7px] ${
              tab === x.id ? "border-accent bg-accent-dim text-accent-text" : "border-transparent hover:bg-bg-hover"
            }`}
          >
            {x.icon}
            {x.label}
          </button>
        ))}
      </nav>

      {tab === "edit" && (
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <div className="grid gap-5 @5xl:grid-cols-[minmax(0,1fr)_250px]">
            <div className="grid gap-[18px]">
              <div className="grid gap-[5px]">
                <label htmlFor="f-type" className="font-semibold">
                  Сценарий
                </label>
                <select id="f-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={field}>
                  {order.map((k) => (
                    <option key={k} value={k}>
                      {types[k]}
                    </option>
                  ))}
                </select>
                <small className="text-[12px] text-fg-muted">Детектор выбирает ответ только в сценарии обращения.</small>
              </div>
              {input("question", "Вопрос клиента", "Обязательно. До 255 знаков; по этому вопросу детектор узнаёт шаблон.", 1)}
              {input("examples", "Как ещё спрашивают", "Необязательно. Одна фраза клиента на строку.", 3)}
              {input("exclude", "Не применять, если", "Необязательно. Одно условие на строку; эти ограничения видит детектор.", 3)}
              {input("text", "Текст шаблона", "Обязательно. Формулировка сохраняется дословно; меняются только подстановки.", 5)}
              {preview("Текст с подстановками · предпросмотр", form.text, "Введите текст выше — подстановки будут выделены здесь.")}
              {input("alt", "Запасной текст", "Необязательно. Например, если город неизвестен. Условия выбора опишите в инструкции.", 3)}
              {preview("Запасной текст · предпросмотр", form.alt, "Запасного текста пока нет.")}
              {input("instruction", "Инструкция модели", "Обязательно. Чем заменить метки в обоих текстах и когда брать запасной текст.", 5)}
              <div className="grid gap-[5px]">
                <label htmlFor="f-reason" className="font-semibold">
                  Почему меняете
                </label>
                <AutoArea id="f-reason" value={reason} rows={2} onChange={(v) => setReason(v.slice(0, 500))} describedBy="f-reason-help" />
                <small id="f-reason-help" className="text-[12px] text-fg-muted">
                  Необязательно. До 500 знаков; комментарий попадёт в историю.
                </small>
              </div>
            </div>
            <aside className="grid content-start gap-4 border-t border-border pt-3.5 @3xl:grid-cols-2 @5xl:grid-cols-1 @5xl:border-t-0 @5xl:border-l @5xl:pt-0 @5xl:pl-5">
              <section>
                <h3 className="font-semibold">Подставляет код</h3>
                <p className="mt-1.5">
                  <Tokens text="{ссылка}" /> — кабинет самозаписи.
                </p>
                <p className="mt-1.5">
                  <Tokens text="{адрес}" /> — адрес, проход, график и телефон салона.
                </p>
                <p className="mt-1.5">
                  <Tokens text="{часы}" /> — часы на сегодня по местному времени салона.
                </p>
                <p className="mt-1.5 text-fg-muted">
                  Адрес и часы требуют известного города. Модель оставляет эти метки как есть; описывать их в инструкции не нужно.
                </p>
              </section>
              <section>
                <h3 className="font-semibold">Подставляет модель</h3>
                <p className="mt-1.5">
                  {marks.length ? (
                    marks.map((m, i) => (
                      <Fragment key={m}>
                        {i > 0 && ", "}
                        <Tokens text={m} />
                        {undescribed.includes(m) && <span className="text-err"> — не описана</span>}
                      </Fragment>
                    ))
                  ) : (
                    <>
                      <Tokens text="{город}" />, <Tokens text="{ближайший}" />
                    </>
                  )}{" "}
                  и любые другие метки — по вашей инструкции.
                </p>
                <p className="mt-1.5 text-fg-muted">
                  Каждая метка из основного и запасного текста должна быть упомянута в инструкции. В готовом ответе таких меток не остаётся.
                </p>
              </section>
              <section>
                <h3 className="font-semibold">Когда шаблон не подходит</h3>
                <p className="mt-1.5 text-fg-muted">
                  Если сообщение содержит ещё вопрос или просьбу, которых ответ не покрывает, детектор выберет «нет».
                </p>
              </section>
            </aside>
          </div>
          {замечание?.key === "*" && <div className="mt-4">{формаЗамечания("*")}</div>}
          {записано?.key === "*" && замечание?.key !== "*" && (
            <p className="mt-3 text-[12px] text-ok">✓ Замечание №{записано.id} к шаблону сохранено — ответ во вкладке «Замечания»</p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-2.5 border-t border-border bg-bg pt-4 pb-2">
            <button
              type="submit"
              className={`${primary} flex-1 @xl:flex-none`}
              disabled={busy || Boolean(template?.archived) || (!dirty && Boolean(template))}
            >
              {template ? "Сохранить изменения" : "Создать шаблон"}
            </button>
            <button type="button" className={`${btn} flex-1 @xl:flex-none`} onClick={() => guard(() => close(null))}>
              К списку
            </button>
            {замечания && (
              <button
                type="button"
                className={`${btn} flex-1 @xl:flex-none`}
                onClick={() => setЗамечание({ key: "*", quote: "" })}
                title="Замечание для модели к шаблону целиком; к куску — выделите его в поле"
              >
                <MessageSquarePlus size={16} /> Замечание к шаблону
              </button>
            )}
            <small className="text-[12px] text-fg-muted">
              {!template ? "Будет создана версия v1" : dirty ? "Есть несохранённые изменения" : `Действующая версия v${template.version}`}
            </small>
          </div>
        </form>
      )}
      {tab === "history" && template && <Versions template={template} busy={busy} revert={revert} show={show} />}
      {tab === "remarks" && template && <AiTemplateRemarks template={template} />}
      {tab === "probe" && <Probe template={template} form={dirty || !template ? form : null} cities={cities} others={others} />}
    </div>
  );
}

function Versions({
  template,
  busy,
  revert,
  show,
}: {
  template: AiTemplate;
  busy: boolean;
  revert: (n: number) => Promise<void>;
  show: (k: keyof AiFields, v: unknown) => string;
}) {
  const versions = useQuery({
    queryKey: ["ai-template-versions", template.id, template.version],
    queryFn: () => loadAiVersions(template.id),
  });
  if (versions.isLoading) return <p className="text-fg-muted">Загружаю историю…</p>;
  if (versions.error) return <Box notice={{ text: (versions.error as Error).message, kind: "error" }} />;
  const list = versions.data ?? [];
  return (
    <div className="grid gap-4">
      <p className="text-fg-muted">
        Версии неизменяемы. Возврат создаст v{template.version + 1} с содержимым выбранной версии; старые записи останутся.
      </p>
      <div className="grid gap-[18px]">
        {list.map((v, i) => {
          const prev = list[i + 1];
          const changes = FIELDS.filter((f) => !prev || String(prev.fields[f.key] ?? "") !== String(v.fields[f.key] ?? ""));
          return (
            <article key={v.number} className="border-b border-border pb-[18px]">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-semibold">
                  <span className={mono}>v{v.number}</span> · {who(v.author_kind, v.author)}
                </h3>
                <small className="text-[12px] text-fg-muted">{when(v.at)}</small>
              </div>
              <p className="text-fg-muted">
                {ACTIONS[v.action] ?? v.action_label}
                {v.reason && ` · ${v.reason}`}
              </p>
              {changes.length > 0 && (
                <details open={v.number === template.version} className="mt-1">
                  <summary className="cursor-pointer">Изменения по полям · {changes.length}</summary>
                  <div className="mt-2.5 grid gap-2">
                    {changes.map((f) => (
                      <div key={f.key}>
                        <strong className="mb-[3px] block">{f.label}</strong>
                        {prev && (
                          <del className="block rounded-[4px] bg-err-bg px-[9px] py-1.5 whitespace-pre-wrap text-err no-underline [overflow-wrap:anywhere]">
                            <span className="sr-only">Удалено: </span>− {show(f.key, prev.fields[f.key])}
                          </del>
                        )}
                        <ins className="mt-0.5 block rounded-[4px] bg-ok-bg px-[9px] py-1.5 whitespace-pre-wrap text-ok no-underline [overflow-wrap:anywhere]">
                          <span className="sr-only">Добавлено: </span>+ {show(f.key, v.fields[f.key])}
                        </ins>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {v.number < template.version ? (
                <div className="mt-2.5">
                  <button type="button" className={btn} disabled={busy || template.archived} onClick={() => void revert(v.number)}>
                    Вернуть содержимое v{v.number}
                  </button>
                </div>
              ) : (
                <small className="text-[12px] text-fg-muted">Действующая версия</small>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

type Run = { result: AiProbe; seconds: number } | { error: string } | null;

function Probe({
  template,
  form,
  cities,
  others,
}: {
  template: AiTemplate | undefined;
  form: AiFields | null;
  cities: { id: number; name: string }[];
  others: AiTemplate[];
}) {
  const own = form ?? (template ? pick(template) : null);
  const [text, setText] = useState(own?.question ?? "");
  const [city, setCity] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [run, setRun] = useState<Run>(null);
  const abort = useRef<AbortController | null>(null);
  const formKey = JSON.stringify(form);
  // Сообщение, город или форма поменялись — идущая проба отменяется, прежний результат уже не о том.
  useEffect(() => {
    abort.current?.abort();
    setLoading(false);
    setRun(null);
  }, [text, city, formKey]);
  const start = async () => {
    if (!text.trim()) {
      setRun({ error: "Введите сообщение клиента" });
      return;
    }
    abort.current?.abort();
    const ctrl = new AbortController();
    abort.current = ctrl;
    setLoading(true);
    setRun(null);
    const t0 = performance.now();
    try {
      const result = await probeAiTemplate({ text, city, template_id: template?.id ?? null, draft: form }, ctrl.signal);
      if (ctrl.signal.aborted) return;
      setRun(
        /без города/.test(result.error)
          ? { error: "Для адреса или часов нужен город. Укажите город и повторите пробу." }
          : { result, seconds: (performance.now() - t0) / 1000 },
      );
    } catch (e) {
      if (!ctrl.signal.aborted) setRun({ error: e instanceof Error ? e.message : String(e) });
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  };
  const other = others.find((t) => t.id !== template?.id && t.type === (own?.type ?? "zapis"));
  const r = run && "result" in run ? run.result : null;
  const kind = r ? (r.picked_this ? "selected" : r.template ? "other" : "none") : null;

  return (
    <div className="grid gap-4">
      <Box
        notice={{
          text: `${form || !template ? "Проба несохранённых изменений" : `Проба сохранённой v${template.version}`}. Версии не создаются; ничего не отправляется клиенту; счётчики не меняются.`,
          kind: "plain",
        }}
      />
      <div className="grid gap-5 @3xl:grid-cols-2">
        <div className="grid content-start gap-4">
          <div className="grid gap-[5px]">
            <label htmlFor="probe-text" className="font-semibold">
              Сообщение клиента
            </label>
            <AutoArea id="probe-text" value={text} rows={5} onChange={setText} describedBy="probe-text-help" />
            <small id="probe-text-help" className="text-[12px] text-fg-muted">
              Для смешанного вопроса попробуйте «{MIXED}»
            </small>
          </div>
          <div className="grid gap-[5px]">
            <label htmlFor="probe-city" className="font-semibold">
              Город для пробы
            </label>
            <select
              id="probe-city"
              value={city ?? ""}
              onChange={(e) => setCity(e.target.value ? Number(e.target.value) : null)}
              className={field}
            >
              <option value="">город неизвестен</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <small className="text-[12px] text-fg-muted">Нужен для подстановки адреса и часов на сегодня.</small>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={primary} disabled={loading} onClick={() => void start()}>
              <Play size={16} /> Запустить пробу
            </button>
            <button type="button" className={btn} onClick={() => setText(own?.question ?? "")}>
              Этот вопрос
            </button>
            <button type="button" className={btn} disabled={!other} onClick={() => other && setText(other.question)}>
              Другой вопрос
            </button>
            <button type="button" className={btn} onClick={() => setText(MIXED)}>
              Смешанный
            </button>
          </div>
          <small className="text-[12px] text-fg-muted">
            Проба идёт через тот же детектор и ту же модель, что кнопка AI в «Чатах». Номер открытого шаблона не закрепляет выбор.
          </small>
        </div>
        <div className={`${card} self-start p-4`} aria-live="polite">
          {loading ? (
            <div role="status" className="flex items-center gap-2 text-fg-muted">
              <Loader2 size={16} className="animate-spin motion-reduce:animate-none" /> Проверяем выбор детектора…
            </div>
          ) : run && "error" in run ? (
            <Box notice={{ text: run.error, kind: "error" }} />
          ) : r && run && "seconds" in run ? (
            <>
              <h3 className={`font-semibold ${kind === "selected" ? "text-ok" : kind === "other" ? "text-warn" : ""}`}>
                {kind === "selected" ? "Выбран этот шаблон" : kind === "other" ? "Выбран другой шаблон" : "Шаблон не выбран"}
              </h3>
              <p className="text-fg-muted">
                {r.template
                  ? `${r.template.id ? `Шаблон #${r.template.id}` : "Новый шаблон"}${
                      kind === "selected" && form ? " · несохранённые изменения" : ` v${r.template.version}`
                    } «${r.template.question}»`
                  : "Ответ по линии сценария"}
              </p>
              <p className="mt-1">{r.type_why}</p>
              <h3 className="mt-[18px] font-semibold">Черновик</h3>
              {r.error ? (
                <Box notice={{ text: `Черновик не вышел: ${r.error}`, kind: "error" }} />
              ) : (
                <div className="mt-2 min-h-16 rounded-[6px] border border-border bg-bg p-3 whitespace-pre-wrap [overflow-wrap:anywhere]">
                  {r.text || "(робот промолчал)"}
                </div>
              )}
              {r.why && <p className="mt-2 text-[12px] text-fg-muted">Пояснение модели: {r.why}</p>}
              <p className="mt-1 text-fg-muted">
                Время {run.seconds.toFixed(1).replace(".", ",")} с · стоимость {r.cost !== null ? `$${r.cost.toFixed(4)}` : "—"}
              </p>
            </>
          ) : (
            <>
              <h3 className="font-semibold">Здесь появится результат</h3>
              <p className="text-fg-muted">
                Детектор может выбрать этот шаблон, другой или ни одного. Номер открытого шаблона не закрепляет выбор.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
